import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

/** The tabs, in display order. */
export const BOOKINGS_TAB_IDS = ['upcoming', 'past'] as const;

export type BookingsTabId = (typeof BOOKINGS_TAB_IDS)[number];

export const DEFAULT_BOOKINGS_TAB: BookingsTabId = 'upcoming';

const TAB_LABEL_IDS: Record<BookingsTabId, string> = {
  upcoming: 'upcoming',
  past: 'past',
};

interface BookingsTabsProps {
  activeTab: BookingsTabId;
  onChange: (tab: BookingsTabId) => void;
  /** Spacing from the caller. Pass a whole literal class string -- Tailwind only emits classes its scanner sees. */
  className?: string;
}

interface IndicatorRect {
  left: number;
  width: number;
}

/*
 * Colours read `primary` (the building's brand variable) rather than a fixed blue: this renders
 * inside a tenant's portal, so a hardcoded accent would show one customer's brand on another's page.
 *
 * ui-atoms' `Tabs` is a segmented pill control and its parts are not `forwardRef`, so a ref handed
 * to `TabsTrigger` is dropped -- the sliding underline is measured off the active trigger.
 */
export const BookingsTabs = ({ activeTab, onChange, className = '' }: BookingsTabsProps): JSX.Element => {
  const intl = useIntl();
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<BookingsTabId, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  const measure = useCallback(() => {
    const active = tabRefs.current.get(activeTab);

    if (active) {
      // Layout coordinates within the relative tablist, so the underline stays aligned when the strip scrolls.
      setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
    }
  }, [activeTab]);

  // Layout effect so the first paint already has the underline in place -- no flash, no slide from zero.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Re-measure when the strip's layout changes: a responsive width, or a locale swap resizing the labels.
  useLayoutEffect(() => {
    const node = listRef.current;

    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => measure());

    observer.observe(node);

    return () => observer.disconnect();
  }, [measure]);

  const setTabRef = useCallback(
    (tab: BookingsTabId) => (element: HTMLButtonElement | null) => {
      if (element) {
        tabRefs.current.set(tab, element);
      } else {
        tabRefs.current.delete(tab);
      }
    },
    [],
  );

  // One handler reading `data-tab`, not an arrow per tab: `react/jsx-no-bind` plus `--max-warnings=0`
  // makes a per-render closure a failed build.
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const tab = event.currentTarget.dataset.tab as BookingsTabId | undefined;

      if (tab) {
        onChange(tab);
      }
    },
    [onChange],
  );

  // WAI-ARIA tabs pattern. The roving tabIndex makes the strip one tab stop, so the arrow keys have to
  // move between tabs. Left/Right wrap, Home/End jump to an end; selection follows focus.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = BOOKINGS_TAB_IDS.indexOf(activeTab);
      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % BOOKINGS_TAB_IDS.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + BOOKINGS_TAB_IDS.length) % BOOKINGS_TAB_IDS.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = BOOKINGS_TAB_IDS.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();

      const nextTab = BOOKINGS_TAB_IDS[nextIndex];

      onChange(nextTab);
      // Focus has to move with the selection, or the next arrow press is handled by the tab just left.
      tabRefs.current.get(nextTab)?.focus();
    },
    [activeTab, onChange],
  );

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={intl.formatMessage({ id: 'my_bookings' })}
      className={`relative flex overflow-x-auto border-b border-border ${className}`}
      data-testid="bookings-tabs"
    >
      {BOOKINGS_TAB_IDS.map(tab => {
        const isActive = tab === activeTab;

        // Whole literal class strings, picked between: Tailwind only emits classes its scanner sees in source.
        const stateClasses = isActive ? 'text-primary' : 'text-muted-foreground hover:text-primary';

        return (
          <button
            key={tab}
            ref={setTabRef(tab)}
            type="button"
            role="tab"
            id={`bookings-tab-${tab}`}
            aria-selected={isActive}
            aria-controls={`bookings-tabpanel-${tab}`}
            tabIndex={isActive ? 0 : -1}
            data-testid={`bookings-tab-${tab}`}
            // Strips the UA button chrome -- this app does not import Tailwind's Preflight. app.css
            // resets appearance/border/background/font for this slot in `base`.
            data-slot="tabs-trigger"
            data-tab={tab}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={`flex-none cursor-pointer whitespace-nowrap px-3 pt-2 pb-4 text-base font-normal transition-colors ${stateClasses}`}
          >
            {intl.formatMessage({ id: TAB_LABEL_IDS[tab] })}
          </button>
        );
      })}

      {/* Decorative; selection lives on each tab's aria-selected. Rendered only once measured, so it mounts in place. */}
      {indicator && (
        <span
          aria-hidden="true"
          data-testid="bookings-tabs-indicator"
          className="pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-primary motion-safe:transition-[left,width] motion-safe:duration-300 motion-safe:ease-out"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
    </div>
  );
};
